import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import {
  Upload as UploadIcon,
  Download as DownloadIcon,
  FileDownload as ExportIcon,
  Delete as DeleteIcon,
  InsertDriveFile as FileIcon,
  Edit as EditIcon,
  AccountTree as GraphIcon,
  ListAlt as ListIcon,
  Add as AddIcon,
  Search as SearchIcon,
  Clear as ClearIcon,
  Link as LinkIcon,
} from '@mui/icons-material';
import DocEditor from './DocEditor';
import DocGraph from './DocGraph';
import { MultiSelectFilter } from '../../../components/shared/StyledComponents';

const API_BASE = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000';

const DOCUMENT_TYPES = ['PRD', 'Roadmap', 'Data Map', 'Architecture', 'Process', 'Other'];
const DOCUMENT_STATUSES = ['Draft', 'In Review', 'Approved', 'Archived'];
const DOC_STATUS_COLORS = { Draft: '#9e9e9e', 'In Review': '#ffa726', Approved: '#66bb6a', Archived: '#b0bec5' };

const TYPE_COLORS_LIGHT = {
  PRD: '#90caf9',
  Roadmap: '#a5d6a7',
  'Data Map': '#ffcc80',
  Architecture: '#f8bbd9',
  Process: '#d1c4e9',
  Other: '#b0bec5',
};

const TYPE_COLORS_DARK = {
  PRD: '#1a3a5c',
  Roadmap: '#1b3a1e',
  'Data Map': '#3a2800',
  Architecture: '#3a1a2e',
  Process: '#2a2040',
  Other: '#2a2a2a',
};

function formatBytes(bytes) {
  if (!bytes) return '—';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function formatDate(iso) {
  try {
    return new Intl.DateTimeFormat('en-AU', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    }).format(new Date(iso));
  } catch {
    return iso;
  }
}

// ── Upload dialog (kept from original) ────────────────────────────────────────

function UploadDocumentDialog({ open, onClose, onUploaded }) {
  const [file, setFile] = useState(null);
  const [name, setName] = useState('');
  const [type, setType] = useState('');
  const [description, setDescription] = useState('');
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');

  const reset = () => {
    setFile(null);
    setName('');
    setType('');
    setDescription('');
    setError('');
  };

  const handleClose = () => { reset(); onClose(); };

  const handleFileChange = (e) => {
    const f = e.target.files[0];
    if (f) {
      setFile(f);
      if (!name) setName(f.name.replace(/\.[^.]+$/, ''));
    }
  };

  const handleSubmit = async () => {
    if (!file) { setError('Please select a file.'); return; }
    if (!name.trim()) { setError('Please enter a document name.'); return; }
    if (!type) { setError('Please select a document type.'); return; }

    setUploading(true);
    setError('');
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('name', name.trim());
      formData.append('type', type);
      formData.append('description', description.trim());

      const res = await fetch(`${API_BASE}/api/doc-hub/upload`, { method: 'POST', body: formData });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.detail || `Upload failed (${res.status})`);
      }
      onUploaded(await res.json());
      handleClose();
    } catch (err) {
      setError(err.message);
    } finally {
      setUploading(false);
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>Upload Document</DialogTitle>
      <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 2 }}>
        {error && <Alert severity="error" onClose={() => setError('')}>{error}</Alert>}
        <Button variant="outlined" component="label" startIcon={<UploadIcon />}>
          {file ? file.name : 'Choose File'}
          <input type="file" hidden onChange={handleFileChange} />
        </Button>
        <TextField label="Document Name" value={name} onChange={(e) => setName(e.target.value)} fullWidth required />
        <FormControl fullWidth required>
          <InputLabel>Type</InputLabel>
          <Select value={type} onChange={(e) => setType(e.target.value)} label="Type">
            {DOCUMENT_TYPES.map((t) => <MenuItem key={t} value={t}>{t}</MenuItem>)}
          </Select>
        </FormControl>
        <TextField label="Description" value={description} onChange={(e) => setDescription(e.target.value)} fullWidth multiline rows={2} />
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={uploading}>Cancel</Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={uploading}
          startIcon={uploading ? <CircularProgress size={16} /> : <UploadIcon />}
        >
          {uploading ? 'Uploading…' : 'Upload'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ── Document card ──────────────────────────────────────────────────────────────

function DocCard({ doc, isReadOnly, onEdit, onDownload, onDelete, deletingId, rosterMap }) {
  const theme = useTheme();
  const TYPE_COLORS = theme.palette.mode === 'dark' ? TYPE_COLORS_DARK : TYPE_COLORS_LIGHT;
  const linkCount =
    (doc.links?.outcomes?.length || 0) +
    (doc.links?.applications?.length || 0) +
    (doc.links?.functions?.length || 0);

  const excerpt = useMemo(() => {
    const text = doc.body || doc.description || '';
    return text.length > 80 ? text.slice(0, 80) + '…' : text;
  }, [doc.body, doc.description]);

  return (
    <Card
      variant="outlined"
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        transition: 'box-shadow 0.15s',
        '&:hover': { boxShadow: 3 },
      }}
    >
      <CardContent sx={{ flex: 1, pb: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, mb: 0.75 }}>
          <Chip
            label={doc.type}
            size="small"
            sx={{
              bgcolor: TYPE_COLORS[doc.type] || '#b0bec5',
              color: 'text.primary',
              fontSize: '0.65rem',
              height: 18,
            }}
          />
          {doc.status && doc.status !== 'Draft' && (
            <Chip
              label={doc.status}
              size="small"
              sx={{
                bgcolor: DOC_STATUS_COLORS[doc.status] || '#9e9e9e',
                color: '#fff',
                fontSize: '0.6rem',
                height: 16,
                fontWeight: 600,
              }}
            />
          )}
          {linkCount > 0 && (
            <Chip
              icon={<LinkIcon sx={{ fontSize: '12px !important' }} />}
              label={linkCount}
              size="small"
              variant="outlined"
              sx={{ height: 18, fontSize: '0.65rem', ml: 'auto' }}
            />
          )}
        </Box>

        <Typography variant="body2" fontWeight={600} sx={{ mb: 0.25, lineHeight: 1.3 }}>
          {doc.name}
        </Typography>

        {doc.owner && rosterMap?.get(doc.owner) && (
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.25, fontSize: '0.65rem' }}>
            {rosterMap.get(doc.owner).name}
          </Typography>
        )}

        {excerpt && (
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', lineHeight: 1.4 }}>
            {excerpt}
          </Typography>
        )}
      </CardContent>

      <Box sx={{ px: 2, pb: 1.5, display: 'flex', alignItems: 'center', gap: 0.5 }}>
        <Typography variant="caption" color="text.secondary" sx={{ flexGrow: 1 }}>
          {formatDate(doc.uploaded_at)}
          {doc.size_bytes > 0 && ` · ${formatBytes(doc.size_bytes)}`}
        </Typography>

        <Tooltip title="Edit">
          <IconButton size="small" onClick={() => onEdit(doc)}>
            <EditIcon fontSize="small" />
          </IconButton>
        </Tooltip>

        {doc.filename && (
          <Tooltip title="Download">
            <IconButton size="small" onClick={() => onDownload(doc)}>
              <DownloadIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}

        {!isReadOnly && (
          <Tooltip title="Delete">
            <span>
              <IconButton
                size="small"
                color="error"
                onClick={() => onDelete(doc)}
                disabled={deletingId === doc.id}
              >
                {deletingId === doc.id ? <CircularProgress size={14} /> : <DeleteIcon fontSize="small" />}
              </IconButton>
            </span>
          </Tooltip>
        )}
      </Box>
    </Card>
  );
}

// ── Main component ─────────────────────────────────────────────────────────────

function DocHub({ isReadOnly = false, isCompact = false, onOpenMap, onOpenRoadmap }) {
  const theme = useTheme();
  const dark = theme.palette.mode === 'dark';
  const [view, setView] = useState('list'); // 'list' | 'editor' | 'graph'
  const [documents, setDocuments] = useState([]);
  const [context, setContext] = useState({ outcomes: [], applications: [], functions: [] });
  const [dataMaps, setDataMaps] = useState([]);
  const [roadmapNodes, setRoadmapNodes] = useState([]);
  const [roster, setRoster] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [filterTypes, setFilterTypes] = useState([]);
  const [filterStatuses, setFilterStatuses] = useState([]);
  const [filterOutcomes, setFilterOutcomes] = useState([]);
  const [filterApps, setFilterApps] = useState([]);
  const [filterOwners, setFilterOwners] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [uploadOpen, setUploadOpen] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const [editingDoc, setEditingDoc] = useState(null); // null = new doc, doc obj = edit existing
  const [sortField, setSortField] = useState('name');
  const [sortDir, setSortDir] = useState('asc');

  // Fetch documents + context once on mount
  const fetchDocuments = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const [docsRes, ctxRes, dmNodesRes, rmNodesRes, rosterRes] = await Promise.all([
        fetch(`${API_BASE}/api/doc-hub/documents`),
        fetch(`${API_BASE}/api/doc-hub/context`),
        fetch(`${API_BASE}/api/data-maps/nodes`),
        fetch(`${API_BASE}/api/roadmaps/nodes`),
        fetch(`${API_BASE}/api/it-roster`),
      ]);
      if (!docsRes.ok) throw new Error(`Failed to load documents (${docsRes.status})`);
      const docs = await docsRes.json();
      setDocuments(docs);

      if (ctxRes.ok) {
        const ctx = await ctxRes.json();
        setContext(ctx);
      }
      if (dmNodesRes.ok) setDataMaps(await dmNodesRes.json());
      if (rmNodesRes.ok) setRoadmapNodes(await rmNodesRes.json());
      if (rosterRes.ok) setRoster(await rosterRes.json());
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchDocuments(); }, [fetchDocuments]);

  const handleDownload = (doc) => {
    const a = document.createElement('a');
    a.href = `${API_BASE}/api/doc-hub/${doc.id}/download`;
    a.download = doc.name;
    a.click();
  };

  const handleDelete = async (doc) => {
    if (!window.confirm(`Delete "${doc.name}"?`)) return;
    setDeletingId(doc.id);
    try {
      if (doc.data_map_id) {
        await fetch(`${API_BASE}/api/data-maps/${doc.data_map_id}`, { method: 'DELETE' }).catch(() => {});
      }
      if (doc.roadmap_id) {
        await fetch(`${API_BASE}/api/roadmaps/${doc.roadmap_id}`, { method: 'DELETE' }).catch(() => {});
      }
      const res = await fetch(`${API_BASE}/api/doc-hub/${doc.id}`, { method: 'DELETE' });
      if (!res.ok) throw new Error(`Delete failed (${res.status})`);
      setDocuments((prev) => prev.filter((d) => d.id !== doc.id));
    } catch (err) {
      setError(err.message);
    } finally {
      setDeletingId(null);
    }
  };

  const openEditor = (doc) => {
    setEditingDoc(doc);
    setView('editor');
  };

  const openNewEditor = () => {
    setEditingDoc(null);
    setView('editor');
  };

  const handleEdit = useCallback((doc) => {
    if (doc.type === 'Data Map' && doc.data_map_id && onOpenMap) {
      onOpenMap(doc.data_map_id);
    } else if (doc.type === 'Roadmap' && doc.roadmap_id && onOpenRoadmap) {
      onOpenRoadmap(doc.roadmap_id);
    } else {
      openEditor(doc);
    }
  }, [onOpenMap, onOpenRoadmap]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleEditorSave = (savedDoc) => {
    setDocuments((prev) => {
      const idx = prev.findIndex((d) => d.id === savedDoc.id);
      if (idx >= 0) {
        const next = [...prev];
        next[idx] = savedDoc;
        return next;
      }
      return [...prev, savedDoc];
    });
    if (savedDoc.type === 'Data Map' && savedDoc.data_map_id && onOpenMap) {
      onOpenMap(savedDoc.data_map_id);
    } else if (savedDoc.type === 'Roadmap' && savedDoc.roadmap_id && onOpenRoadmap) {
      onOpenRoadmap(savedDoc.roadmap_id);
    } else {
      setView('list');
    }
  };

  const handleEditorDelete = (deletedId) => {
    if (deletedId) {
      setDocuments((prev) => prev.filter((d) => d.id !== deletedId));
    }
    setView('list');
  };

  const handleEditorCancel = () => setView('list');

  const handleGraphUpdateDoc = useCallback((updatedDoc) => {
    setDocuments((prev) => {
      const idx = prev.findIndex((d) => d.id === updatedDoc.id);
      if (idx >= 0) {
        const next = [...prev];
        next[idx] = updatedDoc;
        return next;
      }
      return prev;
    });
  }, []);

  const handleGraphEditDoc = useCallback((docId) => {
    const doc = documents.find((d) => d.id === docId);
    if (doc) openEditor(doc);
  }, [documents]);

  const rosterMap = useMemo(() => new Map(roster.map((m) => [m.id, m])), [roster]);

  const ownerFilterOptions = useMemo(() => {
    const ids = new Set(documents.map((d) => d.owner).filter(Boolean));
    return roster.filter((m) => ids.has(m.id)).map((m) => ({ value: m.id, label: m.name }));
  }, [roster, documents]);

  const hasActiveFilters = filterTypes.length > 0 || filterStatuses.length > 0 || filterOutcomes.length > 0 || filterApps.length > 0 || filterOwners.length > 0 || !!searchQuery;

  const clearAllFilters = () => {
    setFilterTypes([]);
    setFilterStatuses([]);
    setFilterOutcomes([]);
    setFilterApps([]);
    setFilterOwners([]);
    setSearchQuery('');
  };

  // Filtered list
  const displayed = useMemo(() => {
    let list = documents;
    if (filterTypes.length > 0)
      list = list.filter((d) => filterTypes.includes(d.type));
    if (filterStatuses.length > 0)
      list = list.filter((d) => filterStatuses.includes(d.status || 'Draft'));
    if (filterOutcomes.length > 0)
      list = list.filter((d) => (d.links?.outcomes || []).some((id) => filterOutcomes.includes(id)));
    if (filterApps.length > 0)
      list = list.filter((d) => (d.links?.applications || []).some((id) => filterApps.includes(id)));
    if (filterOwners.length > 0)
      list = list.filter((d) => filterOwners.includes(d.owner));
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      list = list.filter(
        (d) =>
          d.name.toLowerCase().includes(q) ||
          (d.description || '').toLowerCase().includes(q) ||
          (d.body || '').toLowerCase().includes(q)
      );
    }
    return list;
  }, [documents, filterTypes, filterStatuses, filterOutcomes, filterApps, filterOwners, searchQuery]);

  const sortedDisplayed = useMemo(() => {
    const copy = [...displayed];
    copy.sort((a, b) => {
      let va = sortField === 'updated' ? (a.uploaded_at || '') : (a.name || '');
      let vb = sortField === 'updated' ? (b.uploaded_at || '') : (b.name || '');
      const cmp = va.localeCompare(vb);
      return sortDir === 'asc' ? cmp : -cmp;
    });
    return copy;
  }, [displayed, sortField, sortDir]);

  const toggleSort = (field) => {
    if (sortField === field) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortField(field);
      setSortDir('asc');
    }
  };

  const SortIndicator = ({ field }) => {
    if (sortField !== field) return <span style={{ opacity: 0.3 }}> ↕</span>;
    return <span>{sortDir === 'asc' ? ' ↑' : ' ↓'}</span>;
  };

  // ── Editor view ──────────────────────────────────────────────────────────────
  if (view === 'editor') {
    return (
      <Box sx={{ height: '100%' }}>
        <DocEditor
          doc={editingDoc}
          context={context}
          roster={roster}
          isReadOnly={isReadOnly}
          onSave={handleEditorSave}
          onCancel={handleEditorCancel}
          onDelete={handleEditorDelete}
          onOpenCanvas={onOpenMap}
          onOpenRoadmap={onOpenRoadmap}
        />
      </Box>
    );
  }

  // ── Graph view ───────────────────────────────────────────────────────────────
  if (view === 'graph') {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
        {/* Header + shared filter toolbar */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2, flexWrap: 'wrap' }}>
          <Typography variant="h6" sx={{ flexGrow: 1 }}>Knowledge Graph</Typography>

          <TextField
            size="small"
            placeholder="Search…"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            sx={{ width: 180 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ fontSize: 16 }} />
                </InputAdornment>
              ),
              endAdornment: searchQuery ? (
                <InputAdornment position="end">
                  <IconButton size="small" onClick={() => setSearchQuery('')}>
                    <ClearIcon sx={{ fontSize: 14 }} />
                  </IconButton>
                </InputAdornment>
              ) : null,
            }}
          />

          <MultiSelectFilter
            label="Type"
            value={filterTypes}
            onChange={(e) => setFilterTypes(e.target.value)}
            options={DOCUMENT_TYPES.map((t) => ({ value: t, label: t }))}
            minWidth={120}
          />
          <MultiSelectFilter
            label="Status"
            value={filterStatuses}
            onChange={(e) => setFilterStatuses(e.target.value)}
            options={DOCUMENT_STATUSES.map((s) => ({ value: s, label: s }))}
            minWidth={110}
          />
          <MultiSelectFilter
            label="Outcome"
            value={filterOutcomes}
            onChange={(e) => setFilterOutcomes(e.target.value)}
            options={(context.outcomes || []).map((o) => ({ value: o.id, label: o.name }))}
            minWidth={140}
          />
          <MultiSelectFilter
            label="Application"
            value={filterApps}
            onChange={(e) => setFilterApps(e.target.value)}
            options={(context.applications || []).map((a) => ({ value: a.id, label: a.name }))}
            minWidth={140}
          />
          {ownerFilterOptions.length > 0 && (
            <MultiSelectFilter
              label="Owner"
              value={filterOwners}
              onChange={(e) => setFilterOwners(e.target.value)}
              options={ownerFilterOptions}
              minWidth={130}
            />
          )}

          <Button
            size="small"
            startIcon={<ListIcon />}
            onClick={() => setView('list')}
            variant="outlined"
          >
            List
          </Button>
        </Box>
        <Box sx={{ flex: 1, minHeight: 0 }}>
          <DocGraph
            documents={documents}
            context={context}
            dataMaps={dataMaps}
            roadmapNodes={roadmapNodes}
            isReadOnly={isReadOnly}
            onEditDoc={handleGraphEditDoc}
            onUpdateDoc={handleGraphUpdateDoc}
            filterTypes={filterTypes}
            filterOutcomes={filterOutcomes}
            filterApps={filterApps}
            searchQuery={searchQuery}
          />
        </Box>
      </Box>
    );
  }

  // ── List view ────────────────────────────────────────────────────────────────
  return (
    <Box>
      {/* Toolbar */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 3, flexWrap: 'wrap' }}>
        <Typography variant="h6" sx={{ flexGrow: 1 }}>Documents</Typography>

        <TextField
          size="small"
          placeholder="Search…"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          sx={{ width: 200 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ fontSize: 16 }} />
              </InputAdornment>
            ),
            endAdornment: searchQuery ? (
              <InputAdornment position="end">
                <IconButton size="small" onClick={() => setSearchQuery('')}>
                  <ClearIcon sx={{ fontSize: 14 }} />
                </IconButton>
              </InputAdornment>
            ) : null,
          }}
        />

        <MultiSelectFilter
          label="Type"
          value={filterTypes}
          onChange={(e) => setFilterTypes(e.target.value)}
          options={DOCUMENT_TYPES.map((t) => ({ value: t, label: t }))}
          minWidth={120}
        />
        <MultiSelectFilter
          label="Status"
          value={filterStatuses}
          onChange={(e) => setFilterStatuses(e.target.value)}
          options={DOCUMENT_STATUSES.map((s) => ({ value: s, label: s }))}
          minWidth={110}
        />
        <MultiSelectFilter
          label="Outcome"
          value={filterOutcomes}
          onChange={(e) => setFilterOutcomes(e.target.value)}
          options={(context.outcomes || []).map((o) => ({ value: o.id, label: o.name }))}
          minWidth={140}
        />
        <MultiSelectFilter
          label="Application"
          value={filterApps}
          onChange={(e) => setFilterApps(e.target.value)}
          options={(context.applications || []).map((a) => ({ value: a.id, label: a.name }))}
          minWidth={140}
        />
        {ownerFilterOptions.length > 0 && (
          <MultiSelectFilter
            label="Owner"
            value={filterOwners}
            onChange={(e) => setFilterOwners(e.target.value)}
            options={ownerFilterOptions}
            minWidth={130}
          />
        )}

        {displayed.length > 0 && (
          <Tooltip title="Export visible documents as CSV">
            <IconButton
              size="small"
              onClick={() => {
                const header = ['Name', 'Type', 'Status', 'Owner', 'Tags', 'Description'];
                const rMap = new Map((context.roster || []).map((r) => [r.id, r.name]));
                const rows = displayed.map((d) => [
                  `"${(d.name || '').replace(/"/g, '""')}"`,
                  d.type || '',
                  d.status || 'Draft',
                  rMap.get(d.owner) || d.owner || '',
                  `"${(d.tags || []).join('; ')}"`,
                  `"${(d.description || '').replace(/"/g, '""')}"`,
                ]);
                const csv = [header, ...rows].map((r) => r.join(',')).join('\n');
                const blob = new Blob([csv], { type: 'text/csv' });
                const url = URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url;
                a.download = 'documents.csv';
                a.click();
                URL.revokeObjectURL(url);
              }}
            >
              <ExportIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}

        <Tooltip title="Knowledge Graph">
          <IconButton onClick={() => setView('graph')} size="small">
            <GraphIcon />
          </IconButton>
        </Tooltip>

        <Tooltip title={isReadOnly ? 'Switch to Edit mode to add documents' : ''}>
          <span>
            <Button
              variant="outlined"
              startIcon={<AddIcon />}
              onClick={openNewEditor}
              size="small"
              disabled={isReadOnly}
            >
              New
            </Button>
          </span>
        </Tooltip>
        <Tooltip title={isReadOnly ? 'Switch to Edit mode to upload documents' : ''}>
          <span>
            <Button
              variant="contained"
              startIcon={<UploadIcon />}
              onClick={() => setUploadOpen(true)}
              size="small"
              disabled={isReadOnly}
              sx={{ bgcolor: dark ? '#1a3d39' : '#b2dfdb', color: dark ? '#80cbc4' : 'text.primary', '&:hover': { bgcolor: dark ? '#24524c' : '#80cbc4' } }}
            >
              Upload
            </Button>
          </span>
        </Tooltip>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>
      )}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress />
        </Box>
      ) : displayed.length === 0 ? (
        <Card variant="outlined">
          <CardContent sx={{ textAlign: 'center', py: 6 }}>
            <FileIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }} />
            <Typography color="text.secondary">
              {hasActiveFilters ? 'No documents match your filters.' : 'No documents yet.'}
            </Typography>
            {hasActiveFilters && (
              <Button size="small" onClick={clearAllFilters} sx={{ mt: 1 }}>
                Clear filters
              </Button>
            )}
            {isReadOnly && !hasActiveFilters && (
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                Use the Edit toggle (top right) to add documents.
              </Typography>
            )}
            {!isReadOnly && !hasActiveFilters && (
              <Box sx={{ mt: 2, display: 'flex', justifyContent: 'center', gap: 1 }}>
                <Button variant="outlined" startIcon={<AddIcon />} onClick={openNewEditor}>
                  New Document
                </Button>
                <Button variant="outlined" startIcon={<UploadIcon />} onClick={() => setUploadOpen(true)}>
                  Upload
                </Button>
              </Box>
            )}
          </CardContent>
        </Card>
      ) : (
        <TableContainer component={Paper} variant="outlined" sx={{ mt: 1 }}>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell
                  sx={{ cursor: 'pointer', userSelect: 'none' }}
                  onClick={() => toggleSort('name')}
                >
                  Name<SortIndicator field="name" />
                </TableCell>
                <TableCell>Type</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Owner</TableCell>
                <TableCell align="center">Links</TableCell>
                <TableCell
                  sx={{ cursor: 'pointer', userSelect: 'none' }}
                  onClick={() => toggleSort('updated')}
                >
                  Updated<SortIndicator field="updated" />
                </TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {sortedDisplayed.map((doc) => {
                const owner = doc.owner ? rosterMap.get(doc.owner) : null;
                const linkCount =
                  (doc.links?.outcomes?.length || 0) +
                  (doc.links?.applications?.length || 0) +
                  (doc.links?.functions?.length || 0);
                const statusColor = DOC_STATUS_COLORS[doc.status] || DOC_STATUS_COLORS['Draft'];
                return (
                  <TableRow
                    key={doc.id}
                    hover
                    sx={{ cursor: 'pointer' }}
                    onClick={() => handleEdit(doc)}
                  >
                    <TableCell>
                      <Typography variant="body2" fontWeight={600}>{doc.name}</Typography>
                    </TableCell>
                    <TableCell>
                      <Chip label={doc.type} size="small" sx={{ fontSize: '0.65rem', height: 18 }} />
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={doc.status || 'Draft'}
                        size="small"
                        sx={{
                          fontSize: '0.65rem',
                          height: 18,
                          bgcolor: statusColor + '22',
                          color: statusColor,
                          fontWeight: 600,
                        }}
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant="caption" color="text.secondary">
                        {owner ? owner.name : '—'}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      {linkCount > 0 ? (
                        <Chip label={`${linkCount} linked`} size="small" variant="outlined" sx={{ fontSize: '0.6rem', height: 16 }} />
                      ) : (
                        <Typography variant="caption" color="text.disabled">—</Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Typography variant="caption" color="text.secondary">
                        {doc.uploaded_at ? new Date(doc.uploaded_at).toLocaleDateString('en-AU') : '—'}
                      </Typography>
                    </TableCell>
                    <TableCell align="right" onClick={(e) => e.stopPropagation()}>
                      <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 0.5 }}>
                        {doc.filename && (
                          <Tooltip title="Download">
                            <IconButton size="small" onClick={() => handleDownload(doc)}>
                              <DownloadIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                        {!isReadOnly && (
                          <Tooltip title="Delete">
                            <span>
                              <IconButton
                                size="small"
                                color="error"
                                disabled={deletingId === doc.id}
                                onClick={() => handleDelete(doc)}
                              >
                                {deletingId === doc.id
                                  ? <CircularProgress size={14} />
                                  : <DeleteIcon fontSize="small" />}
                              </IconButton>
                            </span>
                          </Tooltip>
                        )}
                      </Box>
                    </TableCell>
                  </TableRow>
                );
              })}
              {sortedDisplayed.length === 0 && (
                <TableRow>
                  <TableCell colSpan={7} align="center">
                    <Typography variant="caption" color="text.secondary">No documents match the current filters.</Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <UploadDocumentDialog
        open={uploadOpen}
        onClose={() => setUploadOpen(false)}
        onUploaded={(doc) => {
          setDocuments((prev) => [...prev, doc]);
          openEditor(doc);
        }}
      />
    </Box>
  );
}

export default DocHub;
